// components/dashboard/PerformanceChart.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { useAuthContext } from '../../../contexts/AuthContext';

const devLog = (...args) => {
  if (import.meta.env.DEV) console.log(...args);
};
const devWarn = (...args) => {
  if (import.meta.env.DEV) console.warn(...args);
};
const devError = (...args) => {
  if (import.meta.env.DEV) console.error(...args);
};

const VIEWBOX_WIDTH = 800;
const VIEWBOX_HEIGHT = 240;
const LEFT_PADDING = 50; // reserve space for Y-axis labels (moved from right)
const RIGHT_PADDING = 15; // reserve space for right edge
const PLOT_TOP = 10;
const PLOT_BOTTOM = VIEWBOX_HEIGHT - 14;

/** Map a dollar value to SVG y inside the padded plot area. */
const valueToPlotY = (value, adjustedMin, adjustedRange) => {
  const plotHeight = PLOT_BOTTOM - PLOT_TOP;
  return PLOT_BOTTOM - ((value - adjustedMin) / adjustedRange) * plotHeight;
};

/** Y scale with padding; always extends to $0 when data is all positive or all negative. */
const computeYScale = (min, max) => {
  const range = max - min || 1;
  const yPadding = range * 0.1;
  let adjustedMin = min - yPadding;
  let adjustedMax = max + yPadding;
  if (min >= 0) adjustedMin = Math.min(adjustedMin, 0);
  if (max <= 0) adjustedMax = Math.max(adjustedMax, 0);
  const adjustedRange = adjustedMax - adjustedMin || 1;
  return { adjustedMin, adjustedMax, adjustedRange };
};

const PerformanceChart = ({
  symbol = ['ALL'],
  period = '1M',
  viewMode = 'cumulative',
  customDateRange = null,
  /** When set (admin desk), load this user's PnL instead of the signed-in account. */
  forUserId = null,
  /** Visual variant: dashboard (purple) or admin desk (cyan, taller, stronger grid). */
  presentation = 'dashboard',
  /** Daily P&L supplied by the caller ([{ date: 'YYYY-MM-DD', pnl, bySymbol }]) instead of Firestore; null while loading. */
  externalDaily,
}) => {
  const useExternal = externalDaily !== undefined;
  // Get user from auth context
  const { user } = useAuthContext();
  
  // Normalize symbol to array format (handle both string and array for backward compatibility)
  const selectedSymbols = Array.isArray(symbol) ? symbol : [symbol];
  
  // Determine which symbols to include
  // If ALL is selected, use SPX, SPY, QQQ; otherwise use selected symbols
  const targetSymbols = selectedSymbols.includes('ALL') 
    ? ['SPX', 'SPY', 'QQQ'] 
    : selectedSymbols.filter(s => s !== 'ALL');
  
  // Base data: PnL data from Firestore
  const [baseData, setBaseData] = useState([]);
  // Individual symbol data: { SPX: [...], SPY: [...], QQQ: [...] }
  const [individualSymbolData, setIndividualSymbolData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null); // For click interaction
  const [showDetailModal, setShowDetailModal] = useState(false); // Modal for detailed view
  // Always render a single combined/main line (new Portfolio Overview design)
  const showCombinedLine = false;
  const svgRef = useRef(null);
  const isAdminPresentation = presentation === 'admin';
  const CHART_LINE = isAdminPresentation ? '#34c6e8' : '#a855f7';
  const CHART_ACCENT = isAdminPresentation ? '#7ddff5' : '#c084fc';
  const GRID_STROKE = isAdminPresentation ? 'rgba(174, 187, 214, 0.28)' : '#2e1065';
  const GRID_OPACITY = isAdminPresentation ? '1' : '0.45';
  const gradientId = isAdminPresentation ? 'adminChartGradient' : 'chartGradient';

  // Window state for panning
  const [windowStart, setWindowStart] = useState(0); // index in baseData

  // Admin can pass forUserId; otherwise chart always belongs to the signed-in user.
  const userId = forUserId || user?.uid;

  // Extract custom date range values for dependency tracking
  const customStartDate = customDateRange?.startDate 
    ? (customDateRange.startDate instanceof Date ? customDateRange.startDate : new Date(customDateRange.startDate))
    : null;
  const customEndDate = customDateRange?.endDate 
    ? (customDateRange.endDate instanceof Date ? customDateRange.endDate : new Date(customDateRange.endDate))
    : null;

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse YYYY-MM-DD to Date
  const parseDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Markets are closed Sat/Sun — omit those days so the chart does not sit flat at $0.
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Normalize symbol for matching (handles SPXW -> SPX, removes prefixes, etc.)
  const normalizeSymbolForMatching = (symbol) => {
    if (!symbol) return '';
    // Remove common prefixes like $, remove whitespace, uppercase
    let normalized = symbol.toString().trim().toUpperCase().replace(/^\$+/, '');
    // Normalize SPXW to SPX (SPXW is weekly SPX options)
    if (normalized === 'SPXW') {
      normalized = 'SPX';
    }
    return normalized;
  };

  // Fetch PnL data from Firestore
  useEffect(() => {
      devLog('[PnL Debug] ==========================================');
      devLog('[PnL Debug] useEffect triggered');
      devLog('[PnL Debug]   - Component rendered');
      devLog('[PnL Debug]   - userId from auth:', userId);
      devLog('[PnL Debug]   - selectedSymbols:', selectedSymbols);
      devLog('[PnL Debug]   - targetSymbols:', targetSymbols);
      devLog('[PnL Debug]   - period:', period);
      devLog('[PnL Debug] ==========================================');
    
    const fetchPnLData = async () => {
      if (!userId) {
        devLog('[PnL Debug] No userId, skipping fetch');
        setIsLoading(false);
        return;
      }

      if (useExternal && !externalDaily) {
        setIsLoading(true);
        return;
      }

      // Store userId in variable to avoid stale closure
      const currentUserId = userId;

      devLog(`[PnL Debug] ==========================================`);
      devLog(`[PnL Debug] Fetching PnL data:`);
      devLog(`[PnL Debug]   - userId from auth: ${userId}`);
      devLog(`[PnL Debug]   - currentUserId (using): ${currentUserId}`);
      devLog(`[PnL Debug]   - user object:`, user);
      devLog(`[PnL Debug]   - selectedSymbols: ${selectedSymbols.join(', ')}, targetSymbols: ${targetSymbols.join(', ')}`);
      devLog(`[PnL Debug]   - period: ${period}`);
      devLog(`[PnL Debug] ==========================================`);

      try {
        setIsLoading(true);

        // Calculate date range based on custom dates or period
        // If custom dates are provided, use them; otherwise use period
        const now = new Date();
        let startDate = new Date(now);
        let endDate = now;
        
        if (customDateRange && customDateRange.startDate && customDateRange.endDate) {
          // Use custom date range
          startDate = customDateRange.startDate instanceof Date 
            ? customDateRange.startDate 
            : new Date(customDateRange.startDate);
          endDate = customDateRange.endDate instanceof Date 
            ? customDateRange.endDate 
            : new Date(customDateRange.endDate);
          // Ensure endDate is not in the future
          if (endDate > now) {
            endDate = now;
          }
          // Ensure startDate is not after endDate
          if (startDate > endDate) {
            startDate = endDate;
          }
        } else if (period && period !== null) {
          // Use period-based date range only if period is set
          if (period === '1D') {
            startDate.setDate(now.getDate() - 1);
          } else if (period === '1W') {
            startDate.setDate(now.getDate() - 7);
          } else if (period === '1M') {
            startDate.setMonth(now.getMonth() - 1);
          } else if (period === '1Y') {
            startDate.setFullYear(now.getFullYear() - 1);
          } else if (period === 'Custom') {
            setIsLoading(false);
            setBaseData([]);
            setIndividualSymbolData({});
            return;
          } else if (period === 'All') {
            // For 'All', set a very old date to get all data
            startDate.setFullYear(2020, 0, 1);
          } else {
            // Default to 1 week for unknown periods
            startDate.setDate(now.getDate() - 7);
          }
        } else {
          // If period is null and no custom date range, skip fetching (wait for user to select dates)
          devLog('[PnL Debug] ⚠️ No period or custom date range set, skipping fetch');
          setIsLoading(false);
          setBaseData([]);
          setIndividualSymbolData({});
          return;
        }

        if (useExternal) {
          const startStr = formatDate(startDate);
          const endStr = formatDate(endDate);
          const inRange = externalDaily.filter((d) => d.date >= startStr && d.date <= endStr);
          const fill = (pick) => {
            const byDate = new Map(inRange.map((d) => [d.date, pick(d)]));
            const out = [];
            let cumulative = 0;
            const first = inRange.length ? parseDate(inRange[0].date) : null;
            if (!first) return out;
            for (let d = new Date(first); d <= endDate; d.setDate(d.getDate() + 1)) {
              const dateStr = formatDate(d);
              const dailyPnl = byDate.get(dateStr) || 0;
              if (isWeekend(d) && !dailyPnl) continue;
              cumulative += dailyPnl;
              out.push({ date: new Date(d), dateStr, dailyPnl, value: Math.round(cumulative * 100) / 100 });
            }
            return out;
          };
          const filled = fill((d) => d.pnl);
          setBaseData(filled);
          setIndividualSymbolData(Object.fromEntries(targetSymbols.map((s) => [s, fill((d) => d.bySymbol?.[s] || 0)])));
          setWindowStart(Math.max(0, filled.length - getWindowSize(period, customDateRange)));
          return;
        }

        // Query from tbl_user_daily_pnl
        // Query only by userId first, then filter by date in memory
        // This handles cases where document might not have 'date' field or uses document ID pattern
        const dailyPnlQuery = query(
          collection(db, 'tbl_user_daily_pnl'),
          where('userId', '==', currentUserId)
        );

        devLog(`[PnL Debug] ==========================================`);
        devLog(`[PnL Debug] 🔍 STEP 1: About to query tbl_user_daily_pnl`);
        devLog(`[PnL Debug]   - Query userId: ${currentUserId}`);
        devLog(`[PnL Debug]   - Date range: ${formatDate(startDate)} to ${formatDate(endDate)}`);
        devLog(`[PnL Debug]   - Collection: tbl_user_daily_pnl`);
        devLog(`[PnL Debug]   - Query object:`, dailyPnlQuery);
        
        let snapshot;
        try {
          devLog(`[PnL Debug] 🔍 STEP 2: Executing getDocs()...`);
          snapshot = await getDocs(dailyPnlQuery);
          devLog(`[PnL Debug] ✅ STEP 3: Query successful!`);
          devLog(`[PnL Debug]   - Documents found: ${snapshot.size}`);
          devLog(`[PnL Debug]   - Empty: ${snapshot.empty}`);
        } catch (error) {
          devError(`[PnL Debug] ❌ Query error:`, error);
          devError(`[PnL Debug]   - Error message:`, error.message);
          devError(`[PnL Debug]   - Error stack:`, error.stack);
          throw error;
        }
        
        devLog(`[PnL Debug] 📊 Query result: ${snapshot.size} documents found`);
        
        // Log all document IDs and userIds found (for debugging)
        if (snapshot.size > 0) {
          devLog(`[PnL Debug] Found documents:`);
          snapshot.forEach((doc, index) => {
            const data = doc.data();
            devLog(`[PnL Debug]   Doc ${index + 1}:`, {
              docId: doc.id,
              userId: data.userId,
              date: data.date,
              symbols: data.symbols || [],
              totalNetPnl: data.totalNetPnl
            });
          });
        } else {
          devLog(`[PnL Debug] ⚠️ No documents found!`);
          devLog(`[PnL Debug]   - Check if userId in collection matches: ${currentUserId}`);
          devLog(`[PnL Debug]   - Check if documents exist in tbl_user_daily_pnl collection`);
        }
        devLog(`[PnL Debug] ==========================================`);
        
        // Process data: filter by date and symbol, then calculate cumulative PnL
        const dailyData = [];
        let cumulativePnl = 0;

        const tempData = [];
        // Store individual symbol data: { SPX: [{date, dailyPnl, ...}], SPY: [...], QQQ: [...] }
        const individualDataBySymbol = {};
        targetSymbols.forEach(sym => {
          individualDataBySymbol[sym] = [];
        });
        
        let dailyPnlDocsFound = 0;
        let dailyPnlDocsWithSymbol = 0;
        let dailyPnlDocsInDateRange = 0;
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          dailyPnlDocsFound++;
          
          // Extract date from document data or document ID
          // Document ID format: ${userId}_${dateStr} (e.g., "userId_2026-01-12")
          let dateStr = data.date;
          if (!dateStr && doc.id.includes('_')) {
            // Try to extract date from document ID
            const parts = doc.id.split('_');
            if (parts.length >= 2) {
              // Check if last part looks like a date (YYYY-MM-DD)
              const lastPart = parts[parts.length - 1];
              if (/^\d{4}-\d{2}-\d{2}$/.test(lastPart)) {
                dateStr = lastPart;
              }
            }
          }
          
          // Skip if no date found
          if (!dateStr) {
            devWarn(`[PnL Debug] Document ${doc.id} has no date field and cannot extract from ID`);
            return;
          }
          
          // Filter by date range in memory
          const dateStrFormatted = formatDate(startDate);
          const endDateFormatted = formatDate(endDate);
          const isInRange = dateStr >= dateStrFormatted && dateStr <= endDateFormatted;
          
          // Debug: Log date filtering for first few documents
          if (dailyPnlDocsFound <= 10) {
            devLog(`[PnL Debug] Date filter check for doc ${doc.id}:`, {
              docDate: dateStr,
              startDate: dateStrFormatted,
              endDate: endDateFormatted,
              isInRange: isInRange,
              willSkip: !isInRange
            });
          }
          
          if (!isInRange) {
            return; // Skip documents outside date range
          }
          
          dailyPnlDocsInDateRange++;
          const symbols = data.symbols || [];
          
          // Debug: log available symbols and data structure
          if (dailyPnlDocsInDateRange <= 5) {
            devLog(`[PnL Debug] Daily PnL doc ${dailyPnlDocsInDateRange}:`, {
              docId: doc.id,
              date: dateStr,
              userId: data.userId,
              symbols: symbols,
              hasBySymbol: !!data.bySymbol,
              bySymbolKeys: data.bySymbol ? Object.keys(data.bySymbol) : [],
              totalNetPnl: data.totalNetPnl,
              totalTradeCount: data.tradeCount,
              selectedSymbol: symbol
            });
          }
          
          // Check if any of the target symbols exist in this document
          // For multiple symbols, we need to check if at least one matches
          // Normalize target symbols for matching
          const normalizedTargetSymbols = targetSymbols.map(sym => normalizeSymbolForMatching(sym));
          
          const symbolMatches = targetSymbols.map((sym, idx) => {
            const normalizedSym = normalizedTargetSymbols[idx];
            
            // Check in bySymbol object (normalize keys)
            let hasBySymbolData = false;
            if (data.bySymbol) {
              const bySymbolKeys = Object.keys(data.bySymbol);
              hasBySymbolData = bySymbolKeys.some(key => 
                normalizeSymbolForMatching(key) === normalizedSym
              );
            }
            
            // Check in symbols array (normalize values)
            const normalizedSymbols = symbols.map(s => normalizeSymbolForMatching(s));
            const hasInSymbolsArray = normalizedSymbols.includes(normalizedSym);
            
            return {
              symbol: sym,
              normalizedSymbol: normalizedSym,
              hasBySymbolData,
              hasInSymbolsArray,
              matched: hasBySymbolData || hasInSymbolsArray
            };
          });
          
          const hasAnyTargetSymbol = symbolMatches.some(m => m.matched);
          
          // Debug: Log symbol filtering
          if (dailyPnlDocsInDateRange <= 10) {
            devLog(`[PnL Debug] Symbol filter check for date ${dateStr}:`, {
              targetSymbols: targetSymbols,
              availableSymbols: symbols,
              symbolMatches: symbolMatches,
              hasAnyTargetSymbol: hasAnyTargetSymbol,
              hasBySymbol: !!data.bySymbol,
              bySymbolKeys: data.bySymbol ? Object.keys(data.bySymbol) : [],
              totalNetPnl: data.totalNetPnl
            });
          }
          
          // Also log when no match found for debugging
          if (!hasAnyTargetSymbol && dailyPnlDocsInDateRange <= 10) {
            devLog(`[PnL Debug] ⚠️ No match for target symbols ${targetSymbols.join(', ')} in date ${dateStr}`);
          }
          
          if (hasAnyTargetSymbol) {
            dailyPnlDocsWithSymbol++;
            const date = parseDate(dateStr);
            
            // Sum up PnL from all target symbols (for combined line)
            let totalPnl = 0;
            let totalTrades = 0;
            
            // Store individual symbol PnL
            const symbolPnls = {};
            
            if (data.bySymbol && Object.keys(data.bySymbol).length > 0) {
              // Use bySymbol data if available
              // Match using normalized symbols to handle SPXW -> SPX, $SPX -> SPX, etc.
              targetSymbols.forEach(sym => {
                const normalizedSym = normalizeSymbolForMatching(sym);
                // Find matching key in bySymbol (may be SPXW, $SPX, etc.)
                const matchingKey = Object.keys(data.bySymbol).find(key => 
                  normalizeSymbolForMatching(key) === normalizedSym
                );
                
                if (matchingKey && data.bySymbol[matchingKey]) {
                  const symPnl = data.bySymbol[matchingKey].totalNetPnl || 0;
                  const symTrades = data.bySymbol[matchingKey].tradeCount
                    || data.bySymbol[matchingKey].totalTrades
                    || 0;
                  totalPnl += symPnl;
                  totalTrades += symTrades;
                  symbolPnls[sym] = {
                    pnl: symPnl, // dollars at source since #210 — no display scaling
                    trades: symTrades
                  };
                } else {
                  symbolPnls[sym] = {
                    pnl: 0,
                    trades: 0
                  };
                }
              });

              // If bySymbol keys didn't match any selected symbol but the doc
              // is tagged with that symbol, fall back to totalNetPnl (single-symbol).
              if (totalPnl === 0 && (data.totalNetPnl || 0) !== 0) {
                const normalizedSymbols = symbols.map(s => normalizeSymbolForMatching(s));
                const hasSymbolInArray = normalizedTargetSymbols.some(normSym =>
                  normalizedSymbols.includes(normSym)
                );
                if (hasSymbolInArray && targetSymbols.length === 1) {
                  totalPnl = data.totalNetPnl || 0;
                  totalTrades = data.tradeCount || 0;
                  symbolPnls[targetSymbols[0]] = {
                    pnl: totalPnl,
                    trades: totalTrades
                  };
                }
              }
            } else {
              // Fallback: if no bySymbol but symbol is in symbols array, use totalNetPnl
              // This handles cases where data structure doesn't have bySymbol breakdown
              // Use normalized matching for fallback as well
              const normalizedSymbols = symbols.map(s => normalizeSymbolForMatching(s));
              const hasSymbolInArray = normalizedTargetSymbols.some(normSym => 
                normalizedSymbols.includes(normSym)
              );
              if (hasSymbolInArray) {
                // If only one target symbol, use totalNetPnl; otherwise distribute equally
                if (targetSymbols.length === 1) {
                  totalPnl = data.totalNetPnl || 0;
                  totalTrades = data.tradeCount || 0;
                  symbolPnls[targetSymbols[0]] = {
                    pnl: totalPnl,
                    trades: totalTrades
                  };
                } else {
                  // For multiple symbols, try to get from bySymbol first, otherwise use totalNetPnl
                  // This is a fallback case - distribute equally
                  const pnlPerSymbol = (data.totalNetPnl || 0) / targetSymbols.length;
                  const tradesPerSymbol = (data.tradeCount || 0) / targetSymbols.length;
                  totalPnl = data.totalNetPnl || 0;
                  totalTrades = data.tradeCount || 0;
                  targetSymbols.forEach(sym => {
                    symbolPnls[sym] = {
                      pnl: pnlPerSymbol,
                      trades: tradesPerSymbol
                    };
                  });
                }
              } else {
                // No matching symbols, set all to 0
                targetSymbols.forEach(sym => {
                  symbolPnls[sym] = {
                    pnl: 0,
                    trades: 0
                  };
                });
              }
            }
            
            // totalNetPnl is stored in dollars at source since #210 — no display scaling
            const dailyPnl = totalPnl;
            const tradeCount = totalTrades;
            
            devLog(`[PnL Debug] Processing symbols ${targetSymbols.join(', ')} for date ${dateStr}:`, {
              totalPnl: totalPnl,
              totalPnlAfterMultiply: dailyPnl,
              totalTradeCount: tradeCount,
              bySymbol: data.bySymbol ? Object.keys(data.bySymbol) : [],
              targetSymbols: targetSymbols,
              hasData: dailyPnl !== 0 || tradeCount !== 0,
              dataSource: data.bySymbol ? 'bySymbol' : (symbols.includes(targetSymbols[0]) ? 'symbolsArray' : 'none')
            });
            
            // Store combined data
            tempData.push({
              date,
              dateStr: dateStr,
              dailyPnl,
              tradeCount
            });
            
            // Store individual symbol data
            targetSymbols.forEach(sym => {
              if (!individualDataBySymbol[sym]) {
                individualDataBySymbol[sym] = [];
              }
              individualDataBySymbol[sym].push({
                date,
                dateStr: dateStr,
                dailyPnl: symbolPnls[sym]?.pnl || 0,
                tradeCount: symbolPnls[sym]?.trades || 0
              });
            });
          }
        });
        
        devLog(`[PnL Debug] Found ${dailyPnlDocsFound} total docs, ${dailyPnlDocsInDateRange} in date range, ${dailyPnlDocsWithSymbol} with symbols "${targetSymbols.join(', ')}"`);
        
        devLog(`[PnL Debug] Found ${dailyPnlDocsFound} daily PnL docs, ${dailyPnlDocsWithSymbol} with symbols "${targetSymbols.join(', ')}"`);
        
        // Debug: Log individual symbol data
        targetSymbols.forEach(sym => {
          const symData = individualDataBySymbol[sym] || [];
          devLog(`[PnL Debug] Individual data for ${sym}: ${symData.length} points`);
          if (symData.length > 0) {
            devLog(`[PnL Debug]   - First date: ${symData[0].dateStr}, Last date: ${symData[symData.length - 1].dateStr}`);
            devLog(`[PnL Debug]   - Sample data (first 3):`, symData.slice(0, 3).map(d => ({
              date: d.dateStr,
              dailyPnl: d.dailyPnl,
              tradeCount: d.tradeCount
            })));
          }
        });
        
        // Log date range of actual data found
        if (tempData.length > 0) {
          const sortedTempData = [...tempData].sort((a, b) => a.dateStr.localeCompare(b.dateStr));
          devLog(`[PnL Debug] 📅 Actual data date range: ${sortedTempData[0].dateStr} to ${sortedTempData[sortedTempData.length - 1].dateStr}`);
          devLog(`[PnL Debug] 📅 Today's date: ${formatDate(now)}`);
          devLog(`[PnL Debug] 📅 Data points found: ${tempData.length}`);
          devLog(`[PnL Debug] 📅 Sample data (first 3):`, sortedTempData.slice(0, 3).map(d => ({
            date: d.dateStr,
            dailyPnl: d.dailyPnl,
            tradeCount: d.tradeCount
          })));
        } else {
          devLog(`[PnL Debug] ⚠️ No data found for symbols "${targetSymbols.join(', ')}" in period "${period}"`);
          devLog(`[PnL Debug] ⚠️ Date range: ${formatDate(startDate)} to ${formatDate(endDate)}`);
          devLog(`[PnL Debug] ⚠️ Total docs found: ${dailyPnlDocsFound}, In date range: ${dailyPnlDocsInDateRange}, With symbols: ${dailyPnlDocsWithSymbol}`);
        }

        // If no data in selected date range, but we have data available, use the most recent data
        // This handles cases where data is from previous year but user selects recent periods
        // Only do this if NOT using custom date range (user explicitly selected dates)
        if (tempData.length === 0 && dailyPnlDocsFound > 0 && !(customDateRange && customDateRange.startDate && customDateRange.endDate)) {
          devLog(`[PnL Debug] ⚠️ No data in selected date range (${formatDate(startDate)} to ${formatDate(endDate)}), but ${dailyPnlDocsFound} documents exist`);
          devLog(`[PnL Debug]   - Will use most recent available data instead`);
          
          // Collect all available data (without date filter, but keep symbol filter)
          // Note: This fallback only applies when NOT using custom date range
          const allAvailableData = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            let dateStr = data.date;
            if (!dateStr && doc.id.includes('_')) {
              const parts = doc.id.split('_');
              if (parts.length >= 2) {
                const lastPart = parts[parts.length - 1];
                if (/^\d{4}-\d{2}-\d{2}$/.test(lastPart)) {
                  dateStr = lastPart;
                }
              }
            }
            
            if (!dateStr) return;
            
            const symbols = data.symbols || [];
            // Check if any of the target symbols exist in this document
            const hasAnyTargetSymbol = targetSymbols.some(sym => {
              const hasBySymbolData = data.bySymbol && data.bySymbol[sym];
              const hasInSymbolsArray = symbols.includes(sym);
              return hasBySymbolData || hasInSymbolsArray;
            });
            
            // Debug: Log symbol filtering in fallback
            if (allAvailableData.length < 5) {
              devLog(`[PnL Debug] Fallback - Symbol filter check for date ${dateStr}:`, {
                targetSymbols: targetSymbols,
                availableSymbols: symbols,
                hasAnyTargetSymbol: hasAnyTargetSymbol,
                hasBySymbol: !!data.bySymbol,
                bySymbolKeys: data.bySymbol ? Object.keys(data.bySymbol) : [],
                totalNetPnl: data.totalNetPnl
              });
            }
            
            if (hasAnyTargetSymbol) {
              const date = parseDate(dateStr);
              
              // Sum up PnL from all target symbols
              let totalPnl = 0;
              let totalTrades = 0;
              
              if (data.bySymbol) {
                // Use bySymbol data if available
                targetSymbols.forEach(sym => {
                  if (data.bySymbol[sym]) {
                    const symPnl = data.bySymbol[sym].totalNetPnl || 0;
                    const symTrades = data.bySymbol[sym].tradeCount || 0;
                    totalPnl += symPnl;
                    totalTrades += symTrades;
                  }
                });
              } else {
                // Fallback: if no bySymbol but symbol is in symbols array, use totalNetPnl
                // This handles cases where data structure doesn't have bySymbol breakdown
                const hasSymbolInArray = targetSymbols.some(sym => symbols.includes(sym));
                if (hasSymbolInArray) {
                  // If only one target symbol, use totalNetPnl; otherwise distribute equally
                  if (targetSymbols.length === 1) {
                    totalPnl = data.totalNetPnl || 0;
                    totalTrades = data.tradeCount || 0;
                  } else {
                    // For multiple symbols, try to get from bySymbol first, otherwise use totalNetPnl
                    // This is a fallback case
                    totalPnl = data.totalNetPnl || 0;
                    totalTrades = data.tradeCount || 0;
                  }
                }
              }
              
              // totalNetPnl is stored in dollars at source since #210 — no display scaling
              const dailyPnl = totalPnl;
              const tradeCount = totalTrades;
              
              allAvailableData.push({
                date,
                dateStr: dateStr,
                dailyPnl,
                tradeCount
              });
            } else {
              // Debug: Log why document was excluded
              if (allAvailableData.length < 5) {
                const symbolMatches = targetSymbols.map(sym => {
                  const hasBySymbolData = data.bySymbol && data.bySymbol[sym];
                  const hasInSymbolsArray = symbols.includes(sym);
                  return {
                    symbol: sym,
                    hasBySymbolData,
                    hasInSymbolsArray,
                    matched: hasBySymbolData || hasInSymbolsArray
                  };
                });
                devLog(`[PnL Debug] Fallback - Document excluded for date ${dateStr}:`, {
                  targetSymbols: targetSymbols,
                  availableSymbols: symbols,
                  symbolMatches: symbolMatches,
                  hasBySymbol: !!data.bySymbol,
                  bySymbolKeys: data.bySymbol ? Object.keys(data.bySymbol) : [],
                  reason: 'No target symbols found in bySymbol or symbols array'
                });
              }
            }
          });
          
          // Sort by date (newest first)
          allAvailableData.sort((a, b) => b.dateStr.localeCompare(a.dateStr));
          
          // Get the number of days to show based on period
          let daysToShow = 7; // default
          if (period === '1D') {
            daysToShow = 1;
          } else if (period === '1W') {
            daysToShow = 7;
          } else if (period === '1M') {
            daysToShow = 30;
          }
          
          // Take the most recent N days
          const recentData = allAvailableData.slice(0, daysToShow);
          
          // Sort back to oldest first for cumulative calculation
          recentData.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
          
          tempData.push(...recentData);
          
          devLog(`[PnL Debug]   - Found ${allAvailableData.length} total available data points`);
          devLog(`[PnL Debug]   - Showing ${recentData.length} most recent data points (period: ${period}, daysToShow: ${daysToShow})`);
        }

        // Sort by date if not already sorted (in case orderBy failed)
        tempData.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
        
        // Sort individual symbol data by date, then build cumulative values.
        // Without `value`, single-symbol charts render as a flat $0 line.
        targetSymbols.forEach(sym => {
          if (individualDataBySymbol[sym]) {
            individualDataBySymbol[sym].sort((a, b) => a.dateStr.localeCompare(b.dateStr));
            let symbolCumulative = 0;
            individualDataBySymbol[sym] = individualDataBySymbol[sym].map((item) => {
              symbolCumulative += item.dailyPnl || 0;
              return {
                ...item,
                value: symbolCumulative,
              };
            });
          }
        });

        // Calculate cumulative PnL for combined data
        tempData.forEach((item) => {
          cumulativePnl += item.dailyPnl;
          dailyData.push({
            date: item.date,
            value: cumulativePnl,
            dailyPnl: item.dailyPnl,
            tradeCount: item.tradeCount
          });
        });

        // If no data found, try querying from tbl_user_order_backlogs for more granular data
        if (dailyData.length === 0) {
          devLog(`[PnL Debug] No daily PnL data found, trying backlogs query for symbols "${targetSymbols.join(', ')}"`);
          
          // Query from tbl_user_order_backlogs for completed trades
          // Note: Requires composite index on tradeType and exit.filledAt
          // Create index: https://console.firebase.google.com/project/quantum-flo-auto-algo-d3c2b/firestore/indexes
          const startTimestamp = Timestamp.fromDate(startDate);
          const endTimestamp = Timestamp.fromDate(now);
          
          // Query completed trades for target symbols
          // Note: Firestore 'in' query can handle up to 10 values
          let backlogsQuery;
          if (targetSymbols.length === 1) {
            // Single symbol - use equality query
            backlogsQuery = query(
              collection(db, 'tbl_user_order_backlogs'),
              where('userId', '==', currentUserId),
              where('symbol', '==', targetSymbols[0]),
              where('tradeType', '==', 'completed'),
              limit(2000) // Prevent excessive reads
            );
          } else {
            // Multiple symbols - use 'in' query
            backlogsQuery = query(
              collection(db, 'tbl_user_order_backlogs'),
              where('userId', '==', currentUserId),
              where('symbol', 'in', targetSymbols),
              where('tradeType', '==', 'completed'),
              limit(2000) // Prevent excessive reads
            );
          }

          let backlogsSnapshot;
          try {
            backlogsSnapshot = await getDocs(backlogsQuery);
          } catch (error) {
            devError('[PnL Debug] Error querying backlogs:', error);
            // If query fails, try without tradeType filter
            if (targetSymbols.length === 1) {
              backlogsQuery = query(
                collection(db, 'tbl_user_order_backlogs'),
                where('userId', '==', currentUserId),
                where('symbol', '==', targetSymbols[0]),
                limit(2000)
              );
            } else {
              backlogsQuery = query(
                collection(db, 'tbl_user_order_backlogs'),
                where('userId', '==', currentUserId),
                where('symbol', 'in', targetSymbols),
                limit(2000)
              );
            }
            backlogsSnapshot = await getDocs(backlogsQuery);
          }
          
          devLog(`[PnL Debug] Found ${backlogsSnapshot.size} backlog documents for symbols "${targetSymbols.join(', ')}"`);
          
          // Group by date and symbol, calculate daily PnL
          const pnlByDate = new Map();
          const pnlByDateAndSymbol = {}; // { symbol: Map<dateStr, {date, pnl, count}> }
          targetSymbols.forEach(sym => {
            pnlByDateAndSymbol[sym] = new Map();
          });
          
          let validTrades = 0;
          let tradesWithoutExit = 0;
          let tradesWithoutPerformance = 0;
          
          backlogsSnapshot.forEach((doc) => {
            const data = doc.data();
            const symbol = data.symbol;
            
            // Check for exit data
            if (!data.exit?.filledAt) {
              tradesWithoutExit++;
              return;
            }
            
            // Check for performance data
            if (!data.performance?.netPnl) {
              tradesWithoutPerformance++;
              return;
            }

            const exitDate = data.exit.filledAt.toDate ? data.exit.filledAt.toDate() : new Date(data.exit.filledAt);
            const dateStr = formatDate(exitDate);

          // Additional date filter in memory as safety check (should not be needed if query works correctly)
          if (dateStr < formatDate(startDate) || dateStr > formatDate(endDate)) {
            return;
          }

            validTrades++;
            
            // Combined PnL by date
            if (!pnlByDate.has(dateStr)) {
              pnlByDate.set(dateStr, { date: exitDate, pnl: 0, count: 0 });
            }
            const dayData = pnlByDate.get(dateStr);
            const netPnl = data.performance.netPnl || 0;
            dayData.pnl += netPnl;
            dayData.count += 1;
            
            // Individual PnL by symbol and date
            if (symbol && pnlByDateAndSymbol[symbol]) {
              if (!pnlByDateAndSymbol[symbol].has(dateStr)) {
                pnlByDateAndSymbol[symbol].set(dateStr, { date: exitDate, pnl: 0, count: 0 });
              }
              const symbolDayData = pnlByDateAndSymbol[symbol].get(dateStr);
              symbolDayData.pnl += netPnl;
              symbolDayData.count += 1;
            }
          });
          
          devLog(`[PnL Debug] Valid trades: ${validTrades}, Without exit: ${tradesWithoutExit}, Without performance: ${tradesWithoutPerformance}`);

          // Convert to array and calculate cumulative for combined data
          cumulativePnl = 0;
          const sortedDates = Array.from(pnlByDate.entries()).sort((a, b) => 
            a[1].date.getTime() - b[1].date.getTime()
          );

          sortedDates.forEach(([dateStr, dayData]) => {
            cumulativePnl += dayData.pnl;
            dailyData.push({
              date: dayData.date,
              value: cumulativePnl,
              dailyPnl: dayData.pnl,
              tradeCount: dayData.count
            });
          });
          
          // Process individual symbol data from backlogs
          targetSymbols.forEach(sym => {
            const symbolPnlMap = pnlByDateAndSymbol[sym];
            if (symbolPnlMap && symbolPnlMap.size > 0) {
              const symbolSortedDates = Array.from(symbolPnlMap.entries()).sort((a, b) => 
                a[1].date.getTime() - b[1].date.getTime()
              );
              
              let symbolCumulative = 0;
              symbolSortedDates.forEach(([dateStr, dayData]) => {
                symbolCumulative += dayData.pnl;
                if (!individualDataBySymbol[sym]) {
                  individualDataBySymbol[sym] = [];
                }
                individualDataBySymbol[sym].push({
                  date: dayData.date,
                  dateStr: dateStr,
                  dailyPnl: dayData.pnl,
                  tradeCount: dayData.count
                });
              });
            }
          });
          
          // Recalculate cumulative for individual symbols after populating from backlogs
          targetSymbols.forEach(sym => {
            if (individualDataBySymbol[sym] && individualDataBySymbol[sym].length > 0) {
              let symbolCumulative = 0;
              individualDataBySymbol[sym] = individualDataBySymbol[sym].map((item) => {
                symbolCumulative += item.dailyPnl;
                return {
                  ...item,
                  value: symbolCumulative
                };
              });
            }
          });
        }
        
        // Calculate cumulative PnL for individual symbols (after all data sources)
        const processedIndividualData = {};
        targetSymbols.forEach(sym => {
          if (individualDataBySymbol[sym] && individualDataBySymbol[sym].length > 0) {
            // Data already has cumulative value calculated
            processedIndividualData[sym] = individualDataBySymbol[sym];
          } else {
            processedIndividualData[sym] = [];
          }
        });

        // Fill in missing dates with previous cumulative value (for smooth chart)
        devLog(`[PnL Debug] ==========================================`);
        devLog(`[PnL Debug] Final data processing:`);
        devLog(`[PnL Debug]   - dailyData.length: ${dailyData.length}`);
        devLog(`[PnL Debug]   - tempData.length: ${tempData.length}`);
        devLog(`[PnL Debug]   - selectedSymbols: ${selectedSymbols.join(', ')}, targetSymbols: ${targetSymbols.join(', ')}`);
        
        if (dailyData.length > 0) {
          devLog(`[PnL Debug]   - First date: ${formatDate(dailyData[0].date)}`);
          devLog(`[PnL Debug]   - Last date: ${formatDate(dailyData[dailyData.length - 1].date)}`);
          devLog(`[PnL Debug]   - Sample data (first 3):`, dailyData.slice(0, 3));
          
          const filledData = [];
          const start = dailyData[0].date;
          const lastDataDate = dailyData[dailyData.length - 1].date;
          // Always fill through the selected range end (today for period modes),
          // not the last day that happened to have closed trades in Firestore.
          // Missing days keep the previous cumulative value so the chart axis reaches today.
          const finalEnd = endDate;
          const dataMap = new Map(dailyData.map(d => [formatDate(d.date), d]));

          let currentCumulative = 0;
          for (let d = new Date(start); d <= finalEnd; d.setDate(d.getDate() + 1)) {
            if (isWeekend(d)) continue;
            const dateStr = formatDate(d);
            if (dataMap.has(dateStr)) {
              const dayData = dataMap.get(dateStr);
              currentCumulative = dayData.value;
              filledData.push(dayData);
            } else {
              // Fill missing weekdays with previous cumulative value
              filledData.push({
                date: new Date(d),
                value: currentCumulative,
                dailyPnl: 0,
                tradeCount: 0
              });
            }
          }
          
          devLog(`[PnL Debug]   - Filled data from ${formatDate(start)} to ${formatDate(finalEnd)}`);
          devLog(`[PnL Debug]   - Last actual data date: ${formatDate(lastDataDate)}`);
          devLog(`[PnL Debug]   - Today: ${formatDate(now)}`);
          devLog(`[PnL Debug]   - Filled data length: ${filledData.length}`);
          if (filledData.length > 0) {
            devLog(`[PnL Debug]   - First filled date: ${formatDate(filledData[0].date)}`);
            devLog(`[PnL Debug]   - Last filled date: ${formatDate(filledData[filledData.length - 1].date)}`);
            devLog(`[PnL Debug]   - Last 5 filled dates:`, filledData.slice(-5).map(d => formatDate(d.date)));
          }

          devLog(`[PnL Debug]   - filledData.length: ${filledData.length}`);
          devLog(`[PnL Debug]   - Setting baseData with ${filledData.length} data points`);
          if (filledData.length > 0) {
            devLog(`[PnL Debug]   - ⚠️ IMPORTANT: Last date in baseData: ${formatDate(filledData[filledData.length - 1].date)}`);
            devLog(`[PnL Debug]   - ⚠️ IMPORTANT: Last 10 dates:`, filledData.slice(-10).map(d => formatDate(d.date)));
          }
          setBaseData(filledData);
          
          // Fill missing dates for individual symbols
          const filledIndividualData = {};
          targetSymbols.forEach(sym => {
            if (processedIndividualData[sym] && processedIndividualData[sym].length > 0) {
              const symbolFilledData = [];
              const symbolDataMap = new Map(processedIndividualData[sym].map(d => [formatDate(d.date), d]));
              let symbolCurrentCumulative = 0;
              const symbolFinalEnd = finalEnd;
              for (let d = new Date(start); d <= symbolFinalEnd; d.setDate(d.getDate() + 1)) {
                if (isWeekend(d)) continue;
                const dateStr = formatDate(d);
                if (symbolDataMap.has(dateStr)) {
                  const dayData = symbolDataMap.get(dateStr);
                  symbolCurrentCumulative = dayData.value;
                  symbolFilledData.push(dayData);
                } else {
                  symbolFilledData.push({
                    date: new Date(d),
                    value: symbolCurrentCumulative,
                    dailyPnl: 0,
                    tradeCount: 0
                  });
                }
              }
              filledIndividualData[sym] = symbolFilledData;
            } else {
              filledIndividualData[sym] = [];
            }
          });
          setIndividualSymbolData(filledIndividualData);
          
          // Set window start to show latest data points (end of array)
          const size = getWindowSize(period, customDateRange);
          const maxStart = Math.max(0, filledData.length - size);
          devLog(`[PnL Debug]   - Setting windowStart to ${maxStart} (showing last ${size} points)`);
          setWindowStart(maxStart);
        } else {
          devLog(`[PnL Debug]   - ⚠️ No data to display! Setting empty array`);
          setBaseData([]);
          setIndividualSymbolData({});
          setWindowStart(0);
        }
        devLog(`[PnL Debug] ==========================================`);
      } catch (error) {
        console.error('Error fetching PnL data:', error);
        setBaseData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPnLData();
  }, [selectedSymbols.join(','), period, userId, customStartDate, customEndDate, externalDaily]); // Include custom dates in dependencies

  // Compute window size (in days) by period or custom date range
  const getWindowSize = (range, customRange = null) => {
    // If custom date range is provided, calculate days between dates
    if (customRange && customRange.startDate && customRange.endDate) {
      const start = customRange.startDate instanceof Date 
        ? customRange.startDate 
        : new Date(customRange.startDate);
      const end = customRange.endDate instanceof Date 
        ? customRange.endDate 
        : new Date(customRange.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
      return diffDays;
    }
    
    // Use period-based calculation
    const now = new Date();
    if (range === '1W') return 7;
    if (range === '1M') return 30;
    if (range === '1Y') return 365;
    if (range === 'All') {
      // For 'All', return a large number to show all data
      return 3650; // ~10 years
    }
    // 1D and default -> treat as 7 days to allow some panning context
    return 7;
  };

  // Reset window when period changes or baseData updates
  // Always show the latest data points by default
  useEffect(() => {
    if (baseData.length === 0) {
      setWindowStart(0);
      return;
    }
    const size = getWindowSize(period, customDateRange);
    // If data is less than or equal to window size, start from 0 (show all)
    // Otherwise, start from position that shows the last point
    const maxStart = baseData.length <= size ? 0 : baseData.length - size;
    setWindowStart(maxStart);
  }, [period, baseData.length, viewMode, customDateRange]);

  // Visible data derived from baseData and windowStart
  // Logic: 
  // - If 1 symbol selected → use that symbol's data
  // - If 2+ symbols selected → use combined data (baseData) but only show when checkbox NOT checked
  const visibleData = useMemo(() => {
    // Determine which dataset to use
    let dataSource = [];
    
    if (targetSymbols.length === 1) {
      // Single symbol: use individual symbol data
      const symbolData = individualSymbolData[targetSymbols[0]] || [];
      // Fallback to baseData if individual symbol data is empty but baseData has data
      // This handles cases where data exists but individual breakdown might be missing
      if (symbolData.length === 0 && baseData.length > 0) {
        devLog(`[Chart Debug] ⚠️ No individual data for ${targetSymbols[0]}, falling back to baseData (${baseData.length} points)`);
        dataSource = baseData;
      } else {
        dataSource = symbolData;
      }
    } else {
      // Multiple symbols: use combined data (baseData)
      dataSource = baseData;
    }
    
    if (dataSource.length === 0) return [];
    const size = getWindowSize(period, customDateRange);
    
    // If data is less than or equal to window size, show all data
    if (dataSource.length <= size) {
      const slice = dataSource;
      const drawableWidth = VIEWBOX_WIDTH - RIGHT_PADDING - LEFT_PADDING;
      const values = slice.map(p => viewMode === 'daily' ? p.dailyPnl : p.value);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const { adjustedMin, adjustedMax, adjustedRange } = computeYScale(min, max);
      
      return slice.map((p, i) => {
        const rawValue = viewMode === 'daily' ? p.dailyPnl : p.value;
        // Ensure displayValue is a valid number
        const displayValue = (rawValue !== undefined && rawValue !== null && !isNaN(rawValue) && isFinite(rawValue))
          ? Number(rawValue)
          : 0;
        const xPosition = slice.length === 1 
          ? LEFT_PADDING + drawableWidth / 2
          : LEFT_PADDING + (i / Math.max(1, slice.length - 1)) * drawableWidth;
        return {
          ...p,
          displayValue,
          x: xPosition,
          y: valueToPlotY(displayValue, adjustedMin, adjustedRange),
        };
      });
    }
    
    // For data larger than window size, respect the current window start so drag/pan works.
    const maxStart = dataSource.length <= size ? 0 : Math.max(0, dataSource.length - size);
    const start = Math.max(0, Math.min(windowStart, maxStart));
    const end = Math.min(dataSource.length, start + size);
    const slice = dataSource.slice(start, end);
    
    // Debug: Log visible data range
    if (slice.length > 0) {
      devLog(`[Chart Debug] Visible data: ${slice.length} points, from ${formatDate(slice[0].date)} to ${formatDate(slice[slice.length - 1].date)}`);
      devLog(`[Chart Debug] Data source: ${dataSource.length} points, from ${formatDate(dataSource[0].date)} to ${formatDate(dataSource[dataSource.length - 1].date)}`);
      devLog(`[Chart Debug] Window: start=${start}, size=${size}, maxStart=${maxStart}`);
    }

    // Map to SVG coords with padding on both sides
    const drawableWidth = VIEWBOX_WIDTH - RIGHT_PADDING - LEFT_PADDING;
    // Use dailyPnl for daily view, value (cumulative) for cumulative view
    // Filter out invalid values
    const values = slice
      .map(p => viewMode === 'daily' ? p.dailyPnl : p.value)
      .filter(v => v !== undefined && v !== null && !isNaN(v) && isFinite(v));
    
    if (values.length === 0) {
      // Return empty array if no valid values
      return [];
    }
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const { adjustedMin, adjustedMax, adjustedRange } = computeYScale(min, max);
    
    const mappedData = slice.map((p, i) => {
      const rawValue = viewMode === 'daily' ? p.dailyPnl : p.value;
      // Ensure displayValue is a valid number
      const displayValue = (rawValue !== undefined && rawValue !== null && !isNaN(rawValue) && isFinite(rawValue))
        ? Number(rawValue)
        : 0;
      
      // Ensure last point is fully visible by using the full drawableWidth
      // Add LEFT_PADDING to X position to account for left padding
      const xPosition = slice.length === 1 
        ? LEFT_PADDING + drawableWidth / 2 // Center single point
        : LEFT_PADDING + (i / Math.max(1, slice.length - 1)) * drawableWidth;
      return {
      ...p,
        displayValue,
      x: xPosition,
        y: valueToPlotY(displayValue, adjustedMin, adjustedRange),
      };
    });
    
    // Debug: Log first and last point positions
    if (mappedData.length > 0) {
      const firstPoint = mappedData[0];
      const lastPoint = mappedData[mappedData.length - 1];
      devLog(`[Chart Debug] Point positions - First: x=${firstPoint.x.toFixed(1)}, date=${formatDate(firstPoint.date)} | Last: x=${lastPoint.x.toFixed(1)}, date=${formatDate(lastPoint.date)}, VIEWBOX_WIDTH=${VIEWBOX_WIDTH}`);
    }
    
    return mappedData;
  }, [baseData, individualSymbolData, targetSymbols, windowStart, period, viewMode, customDateRange]);

  // Helper function to calculate visible data for a dataset
  const calculateVisibleDataForDataset = (dataset, minValue, maxValue) => {
    if (dataset.length === 0) return [];
    const size = getWindowSize(period, customDateRange);
    const drawableWidth = VIEWBOX_WIDTH - RIGHT_PADDING - LEFT_PADDING;
    
    // Determine slice based on windowStart
    let slice;
    if (dataset.length <= size) {
      slice = dataset;
    } else {
      const maxStart = dataset.length <= size ? 0 : dataset.length - size;
      const start = Math.max(0, Math.min(windowStart, maxStart));
      const end = Math.min(dataset.length, start + size);
      slice = dataset.slice(start, end);
    }
    
    const range = maxValue - minValue || 1;
    const { adjustedMin, adjustedMax, adjustedRange } = computeYScale(minValue, maxValue);
    
    return slice.map((p, i) => {
      const rawValue = viewMode === 'daily' ? p.dailyPnl : p.value;
      // Ensure displayValue is a valid number
      const displayValue = (rawValue !== undefined && rawValue !== null && !isNaN(rawValue) && isFinite(rawValue))
        ? Number(rawValue)
        : 0;
      
      const xPosition = slice.length === 1 
        ? LEFT_PADDING + drawableWidth / 2
        : LEFT_PADDING + (i / Math.max(1, slice.length - 1)) * drawableWidth;
      return {
        ...p,
        displayValue,
        x: xPosition,
        y: valueToPlotY(displayValue, adjustedMin, adjustedRange),
      };
    });
  };

  // Calculate combined line data (sum of all selected symbols)
  const combinedLineData = useMemo(() => {
    if (!showCombinedLine || targetSymbols.length === 0) return [];
    
    // Get all dates from visible data
    if (visibleData.length === 0) return [];
    const dateMap = new Map(visibleData.map(d => [formatDate(d.date), d.date]));
    
    // Calculate combined daily PnL for each date (NOT cumulative)
    const combinedDailyData = [];
    dateMap.forEach((date, dateStr) => {
      let combinedDailyPnl = 0;
      let combinedTradeCount = 0;
      
      targetSymbols.forEach(sym => {
        const symbolData = individualSymbolData[sym] || [];
        const dayData = symbolData.find(d => formatDate(d.date) === dateStr);
        if (dayData) {
          // Always use dailyPnl, not cumulative value
          combinedDailyPnl += dayData.dailyPnl;
          combinedTradeCount += dayData.tradeCount;
        }
      });
      
      combinedDailyData.push({
        date,
        dailyPnl: combinedDailyPnl,
        tradeCount: combinedTradeCount
      });
    });
    
    // Sort by date
    combinedDailyData.sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Calculate cumulative PnL from combined daily PnL
    let cumulativePnl = 0;
    const combinedData = combinedDailyData.map((item) => {
      cumulativePnl += item.dailyPnl;
      return {
        date: item.date,
        value: cumulativePnl,
        dailyPnl: item.dailyPnl,
        tradeCount: item.tradeCount
      };
    });
    
    // Calculate min/max for all datasets (visibleData + individual + combined)
    const allValues = [
      ...visibleData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value),
      ...combinedData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value)
    ];
    targetSymbols.forEach(sym => {
      const symbolData = individualSymbolData[sym] || [];
      allValues.push(...symbolData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value));
    });
    
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    
    const result = calculateVisibleDataForDataset(combinedData, min, max);
    
    // Debug: Log combined line data
    if (showCombinedLine && result.length > 0) {
      devLog('[Combined Line Debug] Combined line data:', {
        dataPoints: result.length,
        firstPoint: result[0],
        lastPoint: result[result.length - 1],
        min,
        max
      });
    } else if (showCombinedLine && result.length === 0) {
      devWarn('[Combined Line Debug] Combined line data is empty!', {
        showCombinedLine,
        targetSymbols,
        individualSymbolDataKeys: Object.keys(individualSymbolData),
        visibleDataLength: visibleData.length,
        combinedDataLength: combinedData.length
      });
    }
    
    return result;
  }, [showCombinedLine, targetSymbols, individualSymbolData, visibleData, windowStart, period, viewMode]);

  // Calculate visible data for individual symbols
  const individualSymbolVisibleData = useMemo(() => {
    const result = {};
    
    // Debug: Log individual symbol data
    devLog('[Individual Symbol Debug] Calculating visible data:', {
      targetSymbols,
      individualSymbolDataKeys: Object.keys(individualSymbolData),
      individualSymbolDataLengths: targetSymbols.map(sym => ({
        symbol: sym,
        length: (individualSymbolData[sym] || []).length
      }))
    });
    
    // Calculate min/max for all datasets first
    const allValues = [
      ...visibleData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value)
    ];
    
    // Add combined line values if checkbox is checked
    if (showCombinedLine && combinedLineData.length > 0) {
      allValues.push(...combinedLineData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value));
    }
    
    targetSymbols.forEach(sym => {
      const symbolData = individualSymbolData[sym] || [];
      if (symbolData.length > 0) {
        const symbolValues = symbolData.map(p => viewMode === 'daily' ? p.dailyPnl : p.value);
        allValues.push(...symbolValues);
      }
    });
    
    const min = allValues.length > 0 ? Math.min(...allValues) : 0;
    const max = allValues.length > 0 ? Math.max(...allValues) : 0;
    
    // Now calculate visible data for each symbol using the same min/max
    targetSymbols.forEach(sym => {
      const symbolData = individualSymbolData[sym] || [];
      if (symbolData.length > 0) {
        result[sym] = calculateVisibleDataForDataset(symbolData, min, max);
        devLog(`[Individual Symbol Debug] ${sym} visible data:`, {
          symbol: sym,
          dataPoints: result[sym].length,
          firstPoint: result[sym][0],
          lastPoint: result[sym][result[sym].length - 1]
        });
      } else {
        result[sym] = [];
        devWarn(`[Individual Symbol Debug] ${sym} has no data!`);
      }
    });
    
    return result;
  }, [targetSymbols, individualSymbolData, visibleData, combinedLineData, showCombinedLine, windowStart, period, viewMode]);

  // Axis labels depending on period using visible range
  const xLabels = useMemo(() => {
    if (visibleData.length === 0) return [];
    const start = visibleData[0].date;
    const end = visibleData[visibleData.length - 1].date;

    if (period === '1Y' || period === 'All') {
      const labels = [];
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      while (cursor <= endMonth) {
        labels.push(new Date(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return labels.map((d) => d.toLocaleString('en-US', { month: 'short' }));
    }

    if (period === '1M') {
      const labels = [];
      const steps = 5; // start + 4 weekly marks
      for (let i = 0; i < steps; i++) {
        const idx = Math.round((i / (steps - 1)) * (visibleData.length - 1));
        labels.push(visibleData[idx].date);
      }
      return labels.map((d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }

    if (period === '1W') {
      return visibleData.map((p) => p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }

    // 1D -> hourly style (but we are showing daily synthetic; still show dates)
    return visibleData.map((p) => p.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }, [visibleData, period]);

  // Build path for visible data
  const pathData = useMemo(() => {
    return visibleData.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  }, [visibleData]);


  // Hover
  const onMouseMove = (e) => {
    if (!svgRef.current || visibleData.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    // Convert mouse position to viewBox coordinates
    // SVG viewBox is VIEWBOX_WIDTH x VIEWBOX_HEIGHT, but actual rendered size is rect.width x rect.height
    const scaleX = VIEWBOX_WIDTH / rect.width;
    const x = (e.clientX - rect.left) * scaleX;
    
    // Find closest point in visibleData
    const closest = visibleData.reduce((prev, curr) => (Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev));
    
    // Debug: Log hovered point date occasionally, especially for dates after Jan 28
    const hoveredDate = new Date(closest.date);
    const jan28 = new Date(2026, 0, 28); // January 28, 2026
    if (Math.random() < 0.01 || hoveredDate > jan28) { // Log 1% of the time or always log dates after Jan 28
      devLog(`[Chart Debug] Hovered point date: ${formatDate(closest.date)}, x=${closest.x.toFixed(1)}, mouseX=${x.toFixed(1)}, visibleData range: ${formatDate(visibleData[0].date)} to ${formatDate(visibleData[visibleData.length - 1].date)}`);
    }
    
    setHoveredPoint(closest);
  };

  // Panning via drag
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, isClick: true });

  const onMouseDown = (e) => {
    dragState.current.dragging = true;
    dragState.current.startX = e.clientX;
    dragState.current.startY = e.clientY;
    dragState.current.isClick = true;
  };

  const onMouseUp = (e) => {
    // If it was a click (not drag), handle point click
    if (dragState.current.isClick && !dragState.current.dragging && hoveredPoint) {
      handlePointClick(hoveredPoint);
    }
    dragState.current.dragging = false;
    dragState.current.isClick = false;
  };

  const onMouseLeave = () => {
    dragState.current.dragging = false;
    dragState.current.isClick = false;
    setHoveredPoint(null);
  };

  // Click handler for data points
  const handlePointClick = (point) => {
    setSelectedPoint(point);
    setShowDetailModal(true);
  };

  const onMouseDrag = (e) => {
    if (!dragState.current.dragging || visibleData.length < 2) return;
    const dx = Math.abs(e.clientX - dragState.current.startX);
    const dy = Math.abs(e.clientY - dragState.current.startY);
    
    // If moved more than 5px, consider it a drag (not click)
    if (dx > 5 || dy > 5) {
      dragState.current.isClick = false;
    }
    
    if (dx < 2) return; // small threshold

    // pixels per day (approx)
    const drawableWidth = VIEWBOX_WIDTH - RIGHT_PADDING - LEFT_PADDING;
    const pxPerIndex = drawableWidth / Math.max(1, visibleData.length - 1);
    const shift = Math.round((e.clientX - dragState.current.startX) / pxPerIndex);
    if (shift !== 0) {
      const size = getWindowSize(period, customDateRange);
      const minStart = 0;
      // Calculate maxStart to allow showing the last point
      // If data length <= size, maxStart should be 0 (show all)
      // Otherwise, maxStart = baseData.length - size (to show last point)
      const maxStart = baseData.length <= size ? 0 : baseData.length - size;
      setWindowStart((prev) => {
        const newStart = prev - shift;
        // Clamp between minStart and maxStart
        return Math.min(maxStart, Math.max(minStart, newStart));
      });
      dragState.current.startX = e.clientX; // reset for smooth continuous drag
    }
  };

  const formatTooltipDate = (date) => {
    if (period === '1D') return date.toLocaleTimeString('en-US', { hour: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  // Y-axis scale aligned with visible chart data
  const chartYScale = useMemo(() => {
    if (visibleData.length === 0) return null;
    const values = visibleData
      .map((d) => d.displayValue)
      .filter((v) => v !== undefined && v !== null && !isNaN(v) && isFinite(v));
    if (values.length === 0) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return computeYScale(min, max);
  }, [visibleData]);

  const formatAxisLabel = (value) => {
    if (Math.abs(value) < 1000) return `$${Math.round(value)}`;
    return `$${(value / 1000).toFixed(1)}k`;
  };

  const yAxisLabels = useMemo(() => {
    if (!chartYScale) return [];

    const { adjustedMin, adjustedMax, adjustedRange } = chartYScale;
    const valueToY = (value) => valueToPlotY(value, adjustedMin, adjustedRange);

    const step = adjustedRange / 4;
    const labels = [];
    for (let i = 0; i <= 4; i += 1) {
      const value = adjustedMin + step * i;
      if (Number.isNaN(value) || !Number.isFinite(value)) continue;
      labels.push({
        value,
        y: valueToY(value),
        label: formatAxisLabel(value),
      });
    }

    // Always include $0 on the Y-axis when zero is within the visible range
    if (adjustedMin <= 0 && adjustedMax >= 0) {
      const minGap = adjustedRange * 0.06;
      const withoutNearZero = labels.filter((l) => Math.abs(l.value) > minGap);
      withoutNearZero.push({ value: 0, y: valueToY(0), label: '$0' });
      return withoutNearZero.sort((a, b) => b.value - a.value);
    }

    return labels.sort((a, b) => b.value - a.value);
  }, [chartYScale]);

  const zeroAxisLabel = useMemo(
    () => yAxisLabels.find((label) => Math.abs(label.value) < 0.01) ?? null,
    [yAxisLabels]
  );

  // Calculate tooltip position to avoid blocking chart line
  // Position tooltip above or below the point instead of to the side
  const tooltipPosition = useMemo(() => {
    if (!hoveredPoint) return null;
    
    // Use pixel coordinates directly from hoveredPoint (which are in viewBox space)
    // Convert to percentage for CSS positioning
    const xPercent = (hoveredPoint.x / VIEWBOX_WIDTH) * 100;
    const yPercent = (hoveredPoint.y / VIEWBOX_HEIGHT) * 100;
    
    // Determine if point is in upper or lower half of chart
    // If point is in upper half (y < 50%), show tooltip BELOW
    // If point is in lower half (y >= 50%), show tooltip ABOVE
    const isInUpperHalf = hoveredPoint.y < VIEWBOX_HEIGHT * 0.5;
    
    // Calculate spacing in viewBox pixels, then convert to percentage
    // Tooltip height is approximately 80px, point radius is 5px
    // We need enough space to avoid overlapping the chart line (at least 50px in viewBox space)
    const spacingInViewBox = 50; // pixels in viewBox space
    const spacingPercent = (spacingInViewBox / VIEWBOX_HEIGHT) * 100; // Convert to percentage
    
    if (isInUpperHalf) {
      // Point is in upper half - show tooltip BELOW
      // Position tooltip starting from point's y position + spacing
      // This ensures tooltip is well below the point and chart line
      const tooltipTopPercent = yPercent + spacingPercent;
      return {
        left: `${xPercent}%`,
        top: `${tooltipTopPercent}%`,
        transform: 'translateX(-50%)'
      };
    } else {
      // Point is in lower half - show tooltip ABOVE
      // Position tooltip ending at point's y position - spacing
      // This ensures tooltip is well above the point and chart line
      const tooltipTopPercent = yPercent - spacingPercent;
      return {
        left: `${xPercent}%`,
        top: `${tooltipTopPercent}%`,
        transform: 'translateX(-50%) translateY(-100%)'
      };
    }
  }, [hoveredPoint]);

  // Calculate modal position to avoid blocking chart line
  const modalPosition = useMemo(() => {
    if (!selectedPoint) return null;
    
    const xPercent = (selectedPoint.x / VIEWBOX_WIDTH) * 100;
    const yPercent = (selectedPoint.y / VIEWBOX_HEIGHT) * 100;

    // Keep the detail card inside the chart bounds when the point is near the edges.
    const isNearLeft = selectedPoint.x < VIEWBOX_WIDTH * 0.2;
    const isNearRight = selectedPoint.x > VIEWBOX_WIDTH * 0.8;

    // Check if point is near the top of chart (less than 30% from top)
    // If near top, show modal below; otherwise show above
    const isNearTop = selectedPoint.y < VIEWBOX_HEIGHT * 0.3;

    let transformX = 'translateX(-50%)';
    if (isNearRight) {
      transformX = 'translateX(-100%)';
    } else if (isNearLeft) {
      transformX = 'translateX(0)';
    }

    if (isNearTop) {
      // Show modal below the point
      return {
        left: `${xPercent}%`,
        top: `${yPercent}%`,
        transform: transformX,
        marginTop: '20px'
      };
    } else {
      // Show modal above the point
      return {
        left: `${xPercent}%`,
        top: `${yPercent}%`,
        transform: `${transformX} translateY(-100%)`,
        marginBottom: '20px'
      };
    }
  }, [selectedPoint]);

  return (
    <>
      {/* Chart Container */}
      <div className={`relative mb-1 ${isAdminPresentation ? 'min-h-[340px]' : 'min-h-[260px]'}`}>
        {/* Interactive Chart with panning */}
        <div className="absolute left-0 right-0 top-0 bottom-6">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
            className="w-full h-full cursor-grab active:cursor-grabbing"
            onMouseMove={(e) => {
              onMouseMove(e);
              onMouseDrag(e);
            }}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
          >
            {/* Grid lines (same y as axis labels; $0 rendered separately below area) */}
            {yAxisLabels
              .filter((label) => Math.abs(label.value) > 0)
              .map((label, i) => (
                <line
                  key={`grid-${label.value}-${i}`}
                  x1={LEFT_PADDING}
                  y1={label.y}
                  x2={VIEWBOX_WIDTH - RIGHT_PADDING}
                  y2={label.y}
                  stroke={GRID_STROKE}
                  strokeWidth="1"
                  strokeDasharray={isAdminPresentation ? '3,6' : '4,4'}
                  opacity={GRID_OPACITY}
                />
              ))}

            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={CHART_LINE} stopOpacity={isAdminPresentation ? '0.55' : '0.45'} />
                <stop offset="55%" stopColor={CHART_LINE} stopOpacity={isAdminPresentation ? '0.16' : '0.12'} />
                <stop offset="100%" stopColor={CHART_LINE} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Area under curve */}
            {visibleData.length > 0 && (
              <path
                d={`${pathData} L ${visibleData[visibleData.length - 1]?.x || LEFT_PADDING} ${zeroAxisLabel?.y ?? PLOT_BOTTOM} L ${LEFT_PADDING} ${zeroAxisLabel?.y ?? PLOT_BOTTOM} Z`}
                fill={`url(#${gradientId})`}
              />
            )}

            {/* $0 baseline — same y as the $0 axis label, styled white dashed */}
            {zeroAxisLabel ? (
              <line
                x1={LEFT_PADDING}
                y1={zeroAxisLabel.y}
                x2={VIEWBOX_WIDTH - RIGHT_PADDING}
                y2={zeroAxisLabel.y}
                stroke="#ffffff"
                strokeWidth="1"
                strokeDasharray="6,5"
                opacity="0.9"
              />
            ) : null}

            {/* Main line */}
            {visibleData.length > 0 && (
              <path
                d={pathData}
                fill="none"
                stroke={CHART_LINE}
                strokeWidth={isAdminPresentation ? '3' : '2.5'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Y-axis labels — drawn last, same y as grid / $0 line */}
            {yAxisLabels
              .filter((label) => label && label.label && label.label !== 'NaN' && !label.label.includes('NaN'))
              .map((label, idx) => (
                <text
                  key={`${label.value}-${idx}`}
                  x={LEFT_PADDING - 6}
                  y={label.y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill={
                    Math.abs(label.value) < 0.01
                      ? (isAdminPresentation ? 'rgba(234, 243, 255, 0.95)' : 'rgba(233, 213, 255, 0.95)')
                      : (isAdminPresentation ? 'rgba(174, 187, 214, 0.9)' : 'rgba(196, 181, 253, 0.8)')
                  }
                  fontSize="11"
                  fontFamily="ui-sans-serif, system-ui, sans-serif"
                  className="pointer-events-none"
                >
                  {label.label}
                </text>
              ))}

            {/* Hover indicator */}
            {hoveredPoint && (
              <>
                <line
                  x1={hoveredPoint.x}
                  y1="0"
                  x2={hoveredPoint.x}
                  y2={VIEWBOX_HEIGHT}
                  stroke={CHART_ACCENT}
                  strokeWidth="1.5"
                  strokeDasharray="4,4"
                  opacity="0.7"
                  className="pointer-events-none"
                />
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r="6"
                  fill={CHART_LINE}
                  opacity="0.25"
                  className="pointer-events-none"
                />
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r="4"
                  fill={CHART_LINE}
                  stroke="#0b0d2a"
                  strokeWidth="1.5"
                  className="pointer-events-none"
                />
              </>
            )}

            {/* Invisible hit targets for click / hover */}
            {visibleData.map((p, i) => {
              const isSelected = selectedPoint && Math.abs(selectedPoint.x - p.x) < 0.1;
              return (
                <g key={i}>
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="10"
                    fill="transparent"
                    cursor="pointer"
                    onClick={() => handlePointClick(p)}
                    style={{ pointerEvents: 'auto' }}
                  />
                  {isSelected && (
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r="4.5"
                      fill={CHART_LINE}
                      stroke="#0b0d2a"
                      strokeWidth="2"
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                </g>
              );
            })}

            {/* Selected point indicator */}
            {selectedPoint && (
              <line
                x1={selectedPoint.x}
                y1="0"
                x2={selectedPoint.x}
                y2={VIEWBOX_HEIGHT}
                stroke={CHART_ACCENT}
                strokeWidth="1.5"
                opacity="0.85"
                className="pointer-events-none"
              />
            )}
          </svg>
        </div>

        {/* Tooltip */}
        {hoveredPoint && tooltipPosition && (
          <div
            className="absolute bg-[#0b0d2a] border border-purple-500/30 text-white px-2.5 py-1.5 rounded-lg shadow-lg text-[10px] pointer-events-none z-[1] backdrop-blur-sm min-w-[120px] max-w-[150px]"
            style={{
              ...tooltipPosition,
              boxShadow: '0 8px 20px rgba(168, 85, 247, 0.2)',
              padding: '8px 10px',
            }}
          >
            <div className="flex items-center justify-between mb-1 pb-0.5 border-b border-purple-900/50">
              <span className="text-[9px] text-gray-400 uppercase tracking-wide">
                {formatTooltipDate(hoveredPoint.date)}
              </span>
            </div>

            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[9px]">
                  {viewMode === 'daily' ? 'Daily' : 'Cumulative'}
                </span>
                <span
                  className={`font-semibold text-xs ${
                    (hoveredPoint.displayValue ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  {(hoveredPoint.displayValue ?? 0) >= 0 ? '+' : ''}$
                  {(hoveredPoint.displayValue ?? 0).toFixed(2)}
                </span>
              </div>

              {viewMode === 'cumulative' &&
                hoveredPoint.dailyPnl !== undefined &&
                hoveredPoint.dailyPnl !== null && (
                  <div className="flex items-center justify-between pt-0.5 border-t border-purple-900/40">
                    <span className="text-gray-400 text-[9px]">Daily</span>
                    <span
                      className={`text-[10px] font-medium ${
                        (hoveredPoint.dailyPnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {(hoveredPoint.dailyPnl ?? 0) >= 0 ? '+' : ''}$
                      {(hoveredPoint.dailyPnl ?? 0).toFixed(2)}
                    </span>
                  </div>
                )}

              {hoveredPoint.tradeCount !== undefined && hoveredPoint.tradeCount > 0 && (
                <div className="flex items-center justify-between pt-0.5 border-t border-purple-900/40">
                  <span className="text-gray-400 text-[9px]">Trades</span>
                  <span className="text-[10px] font-medium text-purple-400">
                    {hoveredPoint.tradeCount}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedPoint && modalPosition && (
          <div
            className="absolute bg-[#0b0d2a] border border-purple-500/40 text-white rounded-xl shadow-2xl z-20 backdrop-blur-sm min-w-[140px] max-w-[160px]"
            style={{
              ...modalPosition,
              boxShadow: '0 8px 24px rgba(168, 85, 247, 0.25)',
            }}
          >
            <div className="p-2.5">
              <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-purple-900/50">
                <h3 className="text-xs font-bold text-purple-400">Performance Details</h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPoint(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-base leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-1.5">
                <div className="bg-purple-950/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 mb-0.5">Date</div>
                  <div className="text-xs font-semibold">
                    {formatTooltipDate(selectedPoint.date)}
                  </div>
                </div>

                <div className="bg-purple-950/50 rounded-lg p-1.5">
                  <div className="text-[10px] text-gray-400 mb-0.5">
                    {viewMode === 'daily' ? 'Daily P&L' : 'Cumulative P&L'}
                  </div>
                  <div
                    className={`text-sm font-bold ${
                      (selectedPoint.displayValue ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {(selectedPoint.displayValue ?? 0) >= 0 ? '+' : ''}$
                    {(selectedPoint.displayValue ?? 0).toFixed(2)}
                  </div>
                </div>

                {viewMode === 'cumulative' &&
                  selectedPoint.dailyPnl !== undefined &&
                  selectedPoint.dailyPnl !== null && (
                    <div className="bg-purple-950/50 rounded-lg p-1.5">
                      <div className="text-[10px] text-gray-400 mb-0.5">Daily P&L</div>
                      <div
                        className={`text-sm font-bold ${
                          (selectedPoint.dailyPnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {(selectedPoint.dailyPnl ?? 0) >= 0 ? '+' : ''}$
                        {(selectedPoint.dailyPnl ?? 0).toFixed(2)}
                      </div>
                    </div>
                  )}

                {selectedPoint.tradeCount !== undefined && selectedPoint.tradeCount > 0 && (
                  <div className="bg-purple-950/50 rounded-lg p-1.5">
                    <div className="text-[10px] text-gray-400 mb-0.5">Total Trades</div>
                    <div className="text-sm font-bold text-purple-400">{selectedPoint.tradeCount}</div>
                  </div>
                )}

                <div className="pt-1.5 border-t border-purple-900/50">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedPoint(null);
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold py-1 px-2 rounded-lg transition-colors text-[10px]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-x-0 top-0 bottom-6 left-10 flex items-center justify-center bg-[#0b0d2a]/70 rounded-lg">
            <div className="text-purple-300/80 text-sm">Loading PnL data...</div>
          </div>
        )}

        {/* No data message */}
        {!isLoading && visibleData.length === 0 && (
          <div className="absolute inset-x-0 top-0 bottom-6 left-10 flex items-center justify-center">
            <div className="text-gray-500 text-sm text-center">
              No PnL data available for {targetSymbols.join(', ')}
              <br />
              <span className="text-xs">Complete some trades to see your performance</span>
            </div>
          </div>
        )}

        {/* X-axis labels */}
        <div className="absolute left-10 right-0 bottom-0 h-5 flex justify-between items-center pointer-events-none">
          {xLabels.map((label, idx) => (
            <span
              key={idx}
              className="text-[10px] text-gray-400 leading-none tabular-nums whitespace-nowrap"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

export default PerformanceChart;